import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { concat, equals, length } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Loading from '../../../../components/Loading';
import { TreeProvider, useTreeContext } from '../../../../components/treeStructure/treeContext';
import Trees from '../../../../components/treeStructure/Trees';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import withUser from '../../../../withUser';
import { SCOPE_OFFER } from '../../../../_CONST';
import { QAllDocumentsQuery } from '../../../../_graphql/queries/documents/QAllDocuments';
import { Collapse } from '../../../../components';
import useOfferRole from '../../../../hooks/useOfferRole';
import { canEditOfferItem } from '../../../../components/users/utils/roles';
import DocumentTreeRow from './DocumentTreeRow';
import UploadRawDocumentButton from './UploadRawDocumentButton';

const DocumentsList = (props) => {
  const {
    args,
    allDocuments,
    offerStatus,
    reFetch,
    setDocuments,
    isOfferPath,
    setRefetchWidgets,
    folders,
    isLoading,
    isPublished,
    setRefetchTags,
    isLoadingFolders
  } = props;
  const [collapseIsOpen, setCollapseIsOpen] = useState(true);
  const { scope, offerId } = args;
  const { addTree, expandedKeys } = useTreeContext();

  const history = useHistory();

  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);
  const isDraft = equals(offerStatus, 'draft');
  const userCanEditDocuments = canEditOfferItem({
    userIsAdmin,
    userIsOfferEditor,
    scope,
    isDraft
  });

  const handleCreateNewFolder = () => addTree(null, expandedKeys)();

  const isLoadingDocuments = isLoading || isLoadingFolders;

  return (
    <Collapse
      collapseIsOpen={collapseIsOpen}
      defaultActiveKey={['1']}
      setCollapseIsOpen={setCollapseIsOpen}
      className="section-doc"
      title={equals(scope, 'global') ? 'Documents de l\'équipe' : 'Documents de l\'offre'}
      actions={
        userCanEditDocuments && (
          <>
            {collapseIsOpen && (
              <Button
                onClick={handleCreateNewFolder}
                icon={<PlusOutlined />}
              >
                Créer un dossier racine
              </Button>
            )}
            {equals(scope, SCOPE_OFFER) && (
              <UploadRawDocumentButton
                scope="offer"
                offerId={offerId}
                reFetch={reFetch}
              />
            )}
            <Button
              onClick={() => history.push(`/offre/${offerId}/edition/documents/creer`) }
              icon={<PlusOutlined />}
            >
              Créer un document
            </Button>
          </>
        )
      }
    >
      {isLoadingDocuments ? <Loading /> : (
        <Trees
          elements={concat(folders, allDocuments)}
          setElements={setDocuments}
          setRefetchWidgets={setRefetchWidgets}
          userIsAdmin={userCanEditDocuments}
          isPublished={isPublished}
          ElementRow={(
            <DocumentTreeRow
              isOfferPath={isOfferPath}
              {...props}
              originalDocuments={allDocuments}
              refetch={reFetch}
              setRefetchWidgets={setRefetchWidgets}
              setRefetchTags={setRefetchTags}
            />
          )}
        />
      )}
    </Collapse>
  );
};

const DocumentListWrapper = ({ cacheKey, args, setIsLoadingOfferItem, offerStatus, ...restProps }) => {
  const { allDocuments, isLoading, error, reFetch } = useFetchQuery({
    query: QAllDocumentsQuery,
    dataProp: 'allDocuments',
    args,
    defaultData: [],
    cacheKey,
    staleWhileRevalidate: true
  });
  const forceRefresh = useRef(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    setIsLoadingOfferItem && setIsLoadingOfferItem(isLoading);
    if (!isLoading && (isNotNilOrEmpty(allDocuments) || forceRefresh.current)) {
      forceRefresh.current = false;
      setDocuments(allDocuments);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allDocuments]);

  useEffect(() => {
    if (!isLoading && length(documents) !== length(allDocuments)) {
      if (!isFirstLoad) {
        forceRefresh.current = true;
        reFetch();
      } else {
        setIsFirstLoad(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, documents, allDocuments]);

  const { offerId, scope } = args;
  const { userIsAdmin, userIsOfferEditor } = useOfferRole(offerId);

  if (error) return <p>Erreur</p>;

  const isDraft = equals(offerStatus, 'draft');
  const userCanCreateOfferDocument = canEditOfferItem({
    userIsAdmin,
    userIsOfferEditor,
    scope,
    isDraft
  });

  const props = {
    args,
    reFetch,
    offerStatus,
    userCanCreateOfferDocument,
    ...restProps
  };

  return (
    <TreeProvider {...props} elements={documents}>
      <DocumentsList
        allDocuments={documents}
        setDocuments={setDocuments}
        args={args}
        reFetch={reFetch}
        isLoading={isLoading}
        setIsLoadingOfferItem={setIsLoadingOfferItem}
        {...restProps}
        offerStatus={offerStatus}
      />
    </TreeProvider>
  );
};

export default withUser(DocumentListWrapper);
